import { proxy } from "valtio";
import type {
  ChecklistResponseDTO,
  ChecklistStepKey,
} from "@/src/features/onboarding-checklist/types";
import {
  getChecklistStatus,
  dismissCelebrationModal,
} from "@/src/features/onboarding-checklist/services/api";
import {
  getCalendarDismissKey,
  getCelebrationDismissKey,
  getCelebrationDismissPendingKey,
} from "@/src/features/onboarding-checklist/constants/stepMeta";
import {
  trackFfScoreSuccess,
  trackItemDone,
} from "@/src/features/onboarding-checklist/utils/tracking";

/**
 * 起步清单全局状态
 * 参考实现模式: store/modules/home.ts (HomeStore)
 */
export interface SetupChecklistState {
  /** 清单数据正在加载中 */
  loading: boolean;
  /** 清单数据加载失败（3 秒超时或 HTTP 错误） */
  error: boolean;
  /** 清单聚合数据 */
  data: ChecklistResponseDTO | null;
  /** 当前在 Sheet 中展开的 StepId（手风琴单选） */
  activeExpandedStepId: number | null;
  /** Sheet 是否处于打开状态 */
  isSheetOpen: boolean;
  /** 庆祝弹窗是否正在展示 */
  isCelebrationModalOpen: boolean;
  /** 当天 Calendar 信号位是否已划走隐藏 */
  isCalendarCardDismissedToday: boolean;
}

export const SetupChecklistStore = proxy<SetupChecklistState>({
  loading: false,
  error: false,
  data: null,
  activeExpandedStepId: null,
  isSheetOpen: false,
  isCelebrationModalOpen: false,
  isCalendarCardDismissedToday: false,
});

/**
 * 本会话内已由原生确认完成、但后端口径可能尚未同步的步骤（乐观完成）。
 * 仅内存态：WebView 重载 / App 重启即清空，回到后端口径 —— 不落 localStorage，避免长期
 * 掩盖后端打标缺失、与原生入口（顶栏徽标 / Calendar 卡，原生自拉接口）口径长期分叉。
 */
const optimisticCompletedSteps = new Set<ChecklistStepKey>();

/**
 * 把乐观完成项合并进清单数据：后端已完成的以后端为准；后端仍未完成的按本地完成覆盖，
 * 并重算 completedCount / allCompleted / firstIncompleteStepId。
 * showCelebration 保持后端值 —— 庆祝弹窗只认后端口径，不因本地乐观态弹出。
 */
function applyOptimisticSteps(
  data: ChecklistResponseDTO,
): ChecklistResponseDTO {
  if (optimisticCompletedSteps.size === 0) {
    return data;
  }
  let patched = false;
  const steps = data.steps.map((s) => {
    if (s.completed || !optimisticCompletedSteps.has(s.stepKey)) {
      return s;
    }
    patched = true;
    return { ...s, completed: true, completedAt: s.completedAt ?? Date.now() };
  });
  if (!patched) {
    return data;
  }
  const completedCount = steps.filter((s) => s.completed).length;
  return {
    ...data,
    steps,
    completedCount,
    allCompleted: completedCount >= data.totalCount,
    firstIncompleteStepId: steps.find((s) => !s.completed)?.stepId ?? null,
  };
}

/**
 * 上报 onboard_item_done：只上报本会话内观察到的「未完成 → 已完成」转变（PRD 各项完成率与清单完成度的分子）。
 * 首次拉取（没有上一份快照）时已完成的项不报，避免老用户每次打开都重复上报；
 * 同一次刷新里完成多项时按 stepId 升序累加 items_done。
 */
function trackNewlyCompletedSteps(
  prev: ChecklistResponseDTO | null,
  next: ChecklistResponseDTO,
): void {
  if (!prev) {
    return;
  }
  const prevCompleted = new Set(
    prev.steps.filter((s) => s.completed).map((s) => s.stepKey),
  );
  let itemsDone = prev.steps.filter((s) => s.completed).length;
  next.steps
    .filter((s) => s.completed && !prevCompleted.has(s.stepKey))
    .sort((a, b) => a.stepId - b.stepId)
    .forEach((s) => {
      itemsDone += 1;
      trackItemDone(s.stepKey, itemsDone);
      if (s.stepKey === "ff_score") {
        // 出分成功即项 1 打勾（PRD S1.4），分值取清单同一字段
        trackFfScoreSuccess(next.ffScore);
      }
    });
}

/**
 * 清单入口（左上角徽标 / 侧边栏卡 / 信号位卡）是否展示：必须已拿到数据且未全部完成。
 * 加载中、读取失败、未登录时都没有数据 → 整块静默不展示、不占位（PRD），
 * 也就不会顶替原有的会员入口（Upgrade / Renew，收入路径）。与 KMP 一致。
 */
export function shouldShowSetupChecklist(
  data: Pick<ChecklistResponseDTO, "allCompleted"> | null | undefined,
): boolean {
  return data != null && !data.allCompleted;
}

/** 仅测试用：清空乐观完成态 */
export function __resetOptimisticStepsForTest(): void {
  optimisticCompletedSteps.clear();
}

// ═══ Actions ═══

/** 前台恢复、路由切换等被动刷新的最小间隔，避免高频请求 */
const PASSIVE_REFRESH_MIN_INTERVAL_MS = 10_000;

let inflightFetch: Promise<void> | null = null;
let lastFetchedAt = 0;

/** 仅测试用：清空请求去重与节流状态 */
export function __resetFetchStateForTest(): void {
  inflightFetch = null;
  lastFetchedAt = 0;
}

/**
 * 拉取清单聚合状态
 * 3 秒超时由 api.ts 层 AbortController 保障 — AC-5
 *
 * 请求去重：已有请求在途时，普通调用直接复用；forceRefresh（某个动作刚完成，需要看到最新结果）
 * 会等在途请求结束后再发一次，避免拿到动作之前的旧数据。
 */
export function fetchChecklist(forceRefresh = false): Promise<void> {
  if (inflightFetch && !forceRefresh) {
    return inflightFetch;
  }
  const previous = inflightFetch;
  const run = previous ? previous.then(doFetchChecklist) : doFetchChecklist();
  const current: Promise<void> = run.finally(() => {
    if (inflightFetch === current) {
      inflightFetch = null;
    }
  });
  inflightFetch = current;
  return current;
}

/**
 * 被动刷新：页面切回前台、回到产品页、打开清单时调用。
 * 有请求在途或距上次拉取不足间隔时跳过 — PRD S1.2、S1.13（完成财务健康分 / 建 tracker / 激活 goal 后状态要跟上）
 */
export function refreshChecklistIfStale(
  minIntervalMs = PASSIVE_REFRESH_MIN_INTERVAL_MS,
): Promise<void> {
  if (inflightFetch || Date.now() - lastFetchedAt < minIntervalMs) {
    return Promise.resolve();
  }
  return fetchChecklist();
}

async function doFetchChecklist(): Promise<void> {
  SetupChecklistStore.loading = true;
  SetupChecklistStore.error = false;
  try {
    const res = applyOptimisticSteps(await getChecklistStatus());
    trackNewlyCompletedSteps(SetupChecklistStore.data, res);
    SetupChecklistStore.data = res;
    // 初始化首个推荐展开项 — AC-1
    if (SetupChecklistStore.activeExpandedStepId === null) {
      SetupChecklistStore.activeExpandedStepId = res.firstIncompleteStepId ?? 1;
    }
    // 触发庆祝弹窗 — AC-4
    if (res.showCelebration) {
      SetupChecklistStore.isCelebrationModalOpen = true;
    }
  } catch {
    SetupChecklistStore.error = true;
  } finally {
    SetupChecklistStore.loading = false;
    lastFetchedAt = Date.now();
  }
}

/** 打开清单 Sheet（可指定展开的 stepId） */
export function openSheet(targetStepId?: number): void {
  if (targetStepId) {
    SetupChecklistStore.activeExpandedStepId = targetStepId;
  }
  SetupChecklistStore.isSheetOpen = true;
}

/** 关闭清单 Sheet */
export function closeSheet(): void {
  SetupChecklistStore.isSheetOpen = false;
}

/** 手风琴切换：点击已展开的则折叠，点击未展开的则展开它 */
export function toggleExpandStep(stepId: number): void {
  SetupChecklistStore.activeExpandedStepId =
    SetupChecklistStore.activeExpandedStepId === stepId ? null : stepId;
}

/**
 * 信号卡当天划走隐藏
 * localStorage Key 绑定 userId + 日期 — fe-spec §1.3 第 2 条
 */
export function dismissCalendarCardToday(userId: string): void {
  SetupChecklistStore.isCalendarCardDismissedToday = true;
  const today = new Date().toISOString().split("T")[0];
  localStorage.setItem(getCalendarDismissKey(userId), today);
}

/** 检查当天是否已划走（页面加载时调用） */
export function checkCalendarDismissStatus(userId: string): void {
  const saved = localStorage.getItem(getCalendarDismissKey(userId));
  const today = new Date().toISOString().split("T")[0];
  SetupChecklistStore.isCalendarCardDismissedToday = saved === today;
}

/**
 * 庆祝弹窗已读确认
 * 三重防重：内存态 + localStorage + 服务端落库 — AC-4
 */
export async function confirmCelebrationDismiss(userId: string): Promise<void> {
  SetupChecklistStore.isCelebrationModalOpen = false;
  if (SetupChecklistStore.data) {
    SetupChecklistStore.data.showCelebration = false;
  }
  localStorage.setItem(getCelebrationDismissKey(userId), "true");
  const synced = await dismissCelebrationModal();
  if (!synced) {
    localStorage.setItem(getCelebrationDismissPendingKey(userId), "true");
  }
}

/**
 * 补发上次失败的「庆祝页已关闭」落库：服务端从未记录时，换设备会再次弹出庆祝页。
 * 进入应用时调用，成功后清除待补发标记，失败则保留等下次。
 */
export async function syncPendingCelebrationDismiss(
  userId: string,
): Promise<void> {
  const pendingKey = getCelebrationDismissPendingKey(userId);
  if (localStorage.getItem(pendingKey) !== "true") {
    return;
  }
  if (await dismissCelebrationModal()) {
    localStorage.removeItem(pendingKey);
  }
}

/**
 * 通知授权成功后（原生已确认系统权限开启，且已走完 enablePushWithPermission → POST /setting 打标）：
 * 先乐观把 notification 项渲染为已完成，再刷新清单。后端打标存在延迟（或缺失）时，
 * 刷新结果不会把该项回退成未完成（见 applyOptimisticSteps）。
 *
 * 注意：web 不上报 system_notification_enabled —— knotify 的 deviceFirstGrantedAt 按
 * OS-Device-Type（后端只认 PC/PWA/IOS/ANDROID/MAIL）落在 device_status 对应设备条目上且
 * 不可逆，web 工厂头只能发 PC，在 App WebView 内会把手机授权错标到 PC 设备。授权打标
 * 由原生 enablePushWithPermission 链路（OS-Device-Type: IOS/ANDROID）负责。
 */
export async function handleNotificationGranted(): Promise<void> {
  optimisticCompletedSteps.add("notification");
  const current = SetupChecklistStore.data;
  if (current) {
    // 乐观完成就是一次「未完成 → 已完成」：在这里上报，后续刷新里它已是完成态，不会重复上报
    const patchedNow = applyOptimisticSteps(current);
    trackNewlyCompletedSteps(current, patchedNow);
    const notificationStepId = current.steps.find(
      (s) => s.stepKey === "notification",
    )?.stepId;
    const next = applyOptimisticSteps(current);
    SetupChecklistStore.data = next;
    // 当前展开的正是通知项 → 收起并推进到下一个推荐项
    if (
      notificationStepId != null &&
      SetupChecklistStore.activeExpandedStepId === notificationStepId
    ) {
      SetupChecklistStore.activeExpandedStepId = next.firstIncompleteStepId;
    }
  }
  await fetchChecklist(true);
}
